<script setup lang="ts">
import { onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"

import { AxiosError } from "axios"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import LayoutNotAuthed from "@/components/shared/LayoutNotAuthed.vue"
import store from "@/plugins/store"
import partnerService from "@/services/partner"
import { apiGuideMessage } from "@/utils/error"
import { reportSentry } from "@/utils/sentry"

const router = useRouter()
const { t } = useI18n()

const errorMessage = ref("")

const login = async () => {
  store.commit("clearAuth")

  const { id, password, redirect: redirection } = router.currentRoute.value.query

  try {
    const data = await partnerService.signIn({ partnerId: String(id), password: String(password) })

    store.commit("saveToken", data.token)
    // LogIn.vue 와 같은 이유로 await 한다 — partner 로드 전에 대상 화면이 mount 되면
    // partner 를 전제한 컴포넌트(Order.vue 등)가 크래시한다.
    await store.dispatch("loadPartner", { force: true })

    const path = (redirection || "/") as string
    router.replace(path)
  } catch (error) {
    console.error("Login failed", error)
    reportSentry(error)

    // 사유를 안 보여주고 로그인 화면으로 튕기면 왜 실패했는지 아무도 알 수 없다.
    // 서버는 사유를 명시해 준다 — 실제로 온 것은 406 "운영상태가 판매중지인
    // 계정입니다" 였는데(PARTNER-FRONTEND-XT) 화면에는 아무 설명도 없이 빈 로그인
    // 폼만 떴다. 게다가 튕겨 보내는 경로가 `/log-in` 오타라 라우터에 없는 주소였고,
    // catch-all 이 `/` 로 → 다시 `/log_in` 으로 두 번 튕겼다.
    const data = error instanceof AxiosError ? error.response?.data : null
    errorMessage.value = apiGuideMessage(data) || t("shared.errors.unknown")
  }
}

const goToLogIn = () => router.replace("/log_in")

onMounted(() => {
  login()
})
</script>

<template>
  <LayoutNotAuthed v-if="errorMessage">
    <div class="row justify-content-center" id="main-container">
      <div class="col-12 col-lg-6 col-xxl-4">
        <Card>
          <CardTitle>{{ $t("log_in.title") }}</CardTitle>
          <CardContent>
            <div class="text-danger text-center">{{ errorMessage }}</div>
          </CardContent>
          <CardActions displayBlock>
            <Button type="button" :text="$t('log_in.action')" @click="goToLogIn" />
          </CardActions>
        </Card>
      </div>
    </div>
  </LayoutNotAuthed>
  <div v-else></div>
</template>

<style scoped>
#main-container {
  margin-top: 5rem;
  margin-bottom: 5rem;
}

@media (max-width: 991.98px) {
  #main-container {
    margin-top: 2rem;
    margin-bottom: 2rem;
  }
}
</style>
